import type { FontKey } from "./metrics";
import { darkenTo, lightenTo, lum, mix } from "./color";

export type ThemeId = "ledger" | "mono" | "blocks" | "dark" | "compact" | "pastel";

export interface BlockStyle {
  /** tint: course colour mixed into surface; solid: full colour; outline: surface + stroke; pastel: soft fill, deep text */
  fill: "tint" | "solid" | "outline" | "pastel";
  tint: number;
  radius: number;
  bar: number;
  stroke: number;
  inset: number;
  text: "ink" | "on-color" | "deep";
  badge: "solid" | "outline" | "inverse" | "soft";
}

export interface Theme {
  id: ThemeId;
  label: { bs: string; en: string };
  layout: "grid" | "agenda";
  dark: boolean;
  paper: string;
  surface: string;
  ink: string;
  ink2: string;
  ink3: string;
  rule: string;
  ruleSoft: string;
  accent: string;
  hot: string;
  frame: number;
  frameRadius: number;
  headRule: number;
  gridLine: number;
  halfHours: boolean;
  zebra: string | null;
  headerRule: "double" | "single" | "thick" | "none";
  fonts: { title: FontKey; kicker: FontKey; day: FontKey; hour: FontKey; blockTitle: FontKey; time: FontKey; meta: FontKey; legendTitle: FontKey; agendaDay: FontKey };
  titleScale: number;
  dayUpper: boolean;
  block: BlockStyle;
  color: (c: string) => string;
}

export const THEMES: Record<ThemeId, Theme> = {
  ledger: {
    id: "ledger", label: { bs: "Papir", en: "Ledger" }, layout: "grid", dark: false,
    paper: "#f2ede3", surface: "#fbf8f2", ink: "#1c1a16", ink2: "#4a463d", ink3: "#8a8374", rule: "#cfc6b4", ruleSoft: "#e2dbcc",
    accent: "#9c1f33", hot: "#e0461a", frame: 1.5, frameRadius: 3, headRule: 1.5, gridLine: 0.75, halfHours: true, zebra: null, headerRule: "double",
    fonts: { title: "displayItalic", kicker: "monoBold", day: "monoBold", hour: "mono", blockTitle: "bodyBold", time: "mono", meta: "mono", legendTitle: "display", agendaDay: "displayItalic" },
    titleScale: 1, dayUpper: true,
    block: { fill: "tint", tint: 0.16, radius: 3, bar: 3, stroke: 0, inset: 1.5, text: "ink", badge: "solid" },
    color: (c) => c,
  },
  mono: {
    id: "mono", label: { bs: "Crno-bijelo", en: "Mono" }, layout: "grid", dark: false,
    paper: "#ffffff", surface: "#ffffff", ink: "#000000", ink2: "#2b2b2b", ink3: "#6b6b6b", rule: "#9a9a9a", ruleSoft: "#d6d6d6",
    accent: "#000000", hot: "#000000", frame: 1, frameRadius: 0, headRule: 1, gridLine: 0.5, halfHours: true, zebra: null, headerRule: "thick",
    fonts: { title: "bodyBold", kicker: "mono", day: "monoBold", hour: "mono", blockTitle: "bodyBold", time: "mono", meta: "mono", legendTitle: "bodyBold", agendaDay: "bodyBold" },
    titleScale: 0.92, dayUpper: true,
    block: { fill: "outline", tint: 0, radius: 0, bar: 3, stroke: 0.8, inset: 1.5, text: "ink", badge: "outline" },
    color: () => "#000000",
  },
  blocks: {
    id: "blocks", label: { bs: "Blokovi", en: "Blocks" }, layout: "grid", dark: false,
    paper: "#f4f1ea", surface: "#f4f1ea", ink: "#141311", ink2: "#3d3a33", ink3: "#7d776a", rule: "#d9d3c5", ruleSoft: "#e6e1d6",
    accent: "#e0461a", hot: "#ff5a1f", frame: 0, frameRadius: 0, headRule: 0, gridLine: 0.75, halfHours: false, zebra: "#ece7dc", headerRule: "none",
    fonts: { title: "bodyBold", kicker: "monoBold", day: "bodyBold", hour: "monoBold", blockTitle: "bodyBold", time: "monoBold", meta: "mono", legendTitle: "bodyBold", agendaDay: "bodyBold" },
    titleScale: 1.12, dayUpper: false,
    block: { fill: "solid", tint: 1, radius: 6, bar: 0, stroke: 0, inset: 2.5, text: "on-color", badge: "inverse" },
    color: (c) => darkenTo(c, 0.2),
  },
  dark: {
    id: "dark", label: { bs: "Noćni", en: "Dark" }, layout: "grid", dark: true,
    paper: "#14130f", surface: "#1c1a15", ink: "#f1ebdd", ink2: "#c9c1b0", ink3: "#8a8374", rule: "#3a362d", ruleSoft: "#2a2721",
    accent: "#f0b45a", hot: "#ff6a3d", frame: 1, frameRadius: 6, headRule: 1, gridLine: 0.75, halfHours: true, zebra: null, headerRule: "single",
    fonts: { title: "displayItalic", kicker: "monoBold", day: "monoBold", hour: "mono", blockTitle: "bodyBold", time: "mono", meta: "mono", legendTitle: "display", agendaDay: "displayItalic" },
    titleScale: 1, dayUpper: true,
    block: { fill: "tint", tint: 0.24, radius: 4, bar: 3, stroke: 0, inset: 1.5, text: "ink", badge: "solid" },
    color: (c) => lightenTo(c, 0.2),
  },
  compact: {
    id: "compact", label: { bs: "Lista", en: "Agenda" }, layout: "agenda", dark: false,
    paper: "#f7f3ea", surface: "#fdfbf6", ink: "#1c1a16", ink2: "#4a463d", ink3: "#8a8374", rule: "#cfc6b4", ruleSoft: "#e4ddcf",
    accent: "#2438e8", hot: "#e0461a", frame: 0, frameRadius: 0, headRule: 1, gridLine: 0.75, halfHours: false, zebra: null, headerRule: "single",
    fonts: { title: "display", kicker: "monoBold", day: "monoBold", hour: "mono", blockTitle: "bodyBold", time: "monoBold", meta: "mono", legendTitle: "bodyBold", agendaDay: "display" },
    titleScale: 1, dayUpper: true,
    block: { fill: "outline", tint: 0, radius: 0, bar: 0, stroke: 0, inset: 0, text: "ink", badge: "solid" },
    color: (c) => c,
  },
  pastel: {
    id: "pastel", label: { bs: "Pastel", en: "Pastel" }, layout: "grid", dark: false,
    paper: "#f7f1ec", surface: "#fffdfb", ink: "#2d2a33", ink2: "#5a5562", ink3: "#9a93a0", rule: "#e6ddd6", ruleSoft: "#efe8e2",
    accent: "#c2577a", hot: "#e0461a", frame: 0, frameRadius: 16, headRule: 0, gridLine: 0.75, halfHours: false, zebra: null, headerRule: "none",
    fonts: { title: "display", kicker: "mono", day: "bodyBold", hour: "mono", blockTitle: "bodyBold", time: "mono", meta: "mono", legendTitle: "bodyBold", agendaDay: "display" },
    titleScale: 1.06, dayUpper: false,
    block: { fill: "pastel", tint: 0.22, radius: 9, bar: 0, stroke: 0, inset: 2.5, text: "deep", badge: "soft" },
    color: (c) => c,
  },
};

export const THEME_IDS = Object.keys(THEMES) as ThemeId[];

export interface BlockColors { fill: string; stroke: string | null; bar: string | null; title: string; text: string; sub: string; badgeBg: string; badgeFg: string; badgeStroke: string | null; mark: string }

export function blockColors(t: Theme, course: string): BlockColors {
  const c = t.color(course);
  const b = t.block;
  switch (b.fill) {
    case "solid": {
      const fg = lum(c) > 0.42 ? t.ink : "#ffffff";
      return { fill: c, stroke: null, bar: null, title: fg, text: fg, sub: mix(fg, c, 0.78), badgeBg: fg, badgeFg: c, badgeStroke: null, mark: fg };
    }
    case "pastel": {
      const deep = darkenTo(mix(c, "#000000", 0.62), 0.06);
      return { fill: mix(c, t.surface, b.tint), stroke: null, bar: null, title: deep, text: mix(deep, t.ink, 0.7), sub: mix(deep, t.surface, 0.72), badgeBg: mix(c, "#ffffff", 0.82), badgeFg: "#ffffff", badgeStroke: null, mark: deep };
    }
    case "outline":
      return { fill: t.surface, stroke: t.ink, bar: t.ink, title: t.ink, text: t.ink2, sub: t.ink3, badgeBg: t.surface, badgeFg: t.ink, badgeStroke: t.ink, mark: t.ink };
    default:
      return { fill: mix(c, t.surface, b.tint), stroke: null, bar: c, title: t.ink, text: t.ink2, sub: t.dark ? mix(t.ink3, c, 0.75) : t.ink3, badgeBg: c, badgeFg: t.dark ? "#14130f" : "#ffffff", badgeStroke: null, mark: c };
  }
}
